import type { IconName } from '@/components/ui/Icon';
import type { HubConditions, HubConditionGroups, HubCondition, HubCapabilities } from '@/shared/api/adminApi';

/**
 * פעולה נעולה כשחסרה יכולת פר-מרכזיה. מחזיר הודעת-נעילה (או null אם פנויה).
 *  - send_whatsapp: דרוש מופע WhatsApp של kali-ai (מוגדר ע"י מנהל-על).
 *  - send_email:   דרושים פרטי SMTP בהגדרות → חברה.
 */
export function actionLock(type: string, caps?: HubCapabilities): string | null {
  if (type === 'send_whatsapp' && caps && !caps.has_whatsapp) {
    return 'דרוש מופע של kali-ai — פנה למנהל המערכת לחיבור WhatsApp למרכזיה.';
  }
  if (type === 'send_email' && caps && !caps.has_smtp) {
    return 'דרוש למלא פרטי דואר יוצא (SMTP) בהגדרות → חברה כדי לשלוח אימיילים.';
  }
  if (type.startsWith('fireberry_') && caps && !caps.has_fireberry) {
    return 'דרוש חיבור לפיירברי — הדלק את המתג FIREBERRY בהגדרות → חברה והזן TokenID.';
  }
  return null;
}

export type EnumOption = { value: string; label: string };

// ערכי enum מאומתים מול payload ה-backend (origin/main).
const CALL_STATUS_ENUM: EnumOption[] = [
  { value: 'ringing', label: 'מצלצלת' },
  { value: 'answered', label: 'נענתה' },
  { value: 'completed', label: 'הושלמה' },
  { value: 'missed', label: 'לא נענתה' },
  { value: 'busy', label: 'תפוס' },
  { value: 'no-answer', label: 'אין מענה' },
  { value: 'failed', label: 'נכשלה' },
];
const DIRECTION_ENUM: EnumOption[] = [
  { value: 'inbound', label: 'נכנסת' },
  { value: 'outbound', label: 'יוצאת' },
  { value: 'internal', label: 'פנימית' },
];
const SENTIMENT_ENUM: EnumOption[] = [
  { value: 'positive', label: 'חיובי' },
  { value: 'neutral', label: 'נייטרלי' },
  { value: 'negative', label: 'שלילי' },
];
const PRIORITY_ENUM: EnumOption[] = [
  { value: 'low', label: 'נמוכה' },
  { value: 'normal', label: 'רגילה' },
  { value: 'high', label: 'גבוהה' },
  { value: 'urgent', label: 'דחופה' },
];
// סטטוס איש-קשר/ליד (מ-API חיצוני: lead.created/updated/status_changed → Contact).
const CONTACT_STATUS_ENUM: EnumOption[] = [
  { value: 'active', label: 'פעיל' },
  { value: 'lead', label: 'ליד' },
  { value: 'customer', label: 'לקוח' },
  { value: 'archived', label: 'בארכיון' },
];
// סטטוס ליד-חייגן (lead.status_changed מהחייגן → CampaignLead).
const DIALER_LEAD_STATUS_ENUM: EnumOption[] = [
  { value: 'queued', label: 'בתור' },
  { value: 'calling', label: 'בחיוג' },
  { value: 'connected', label: 'מחובר' },
  { value: 'completed', label: 'הושלם' },
  { value: 'failed', label: 'נכשל' },
  { value: 'dnc', label: 'לא להתקשר' },
];

// אירועים לפי משפחה — כדי לפתור enum תלוי-אירוע (אותו path, ערכים שונים).
const CALL_EVENTS = new Set(['call.started', 'call.answered', 'call.ended', 'call.missed', 'call.recording.ready']);
const CONTACT_LEAD_EVENTS = new Set(['contact.created', 'contact.updated', 'lead.created', 'lead.updated']);

/**
 * ערכי enum לשדה נתון, תלוי-אירוע (אם ידוע). מפתח = field path בלי {{}}.
 * ⚠️ data.status מקבל enum שונה לפי האירוע: call.* → סטטוס שיחה; contact/lead →
 * סטטוס איש-קשר; lead.status_changed → סטטוס חייגן. שדות עם enum גלובלי
 * (direction/sentiment/priority) לא תלויים באירוע.
 * דיספוזיציות (data.disposition) הן דינמיות פר-מרכזיה — לא כאן (טקסט חופשי).
 */
export function enumForField(field: string, event?: string): EnumOption[] | undefined {
  switch (field) {
    case 'data.direction': return DIRECTION_ENUM;
    case 'data.sentiment': return SENTIMENT_ENUM;
    case 'data.priority': return PRIORITY_ENUM;
    case 'data.status':
      if (!event || event === '*') return undefined; // עמום — עדיף טקסט חופשי
      if (CALL_EVENTS.has(event)) return CALL_STATUS_ENUM;
      if (CONTACT_LEAD_EVENTS.has(event)) return CONTACT_STATUS_ENUM;
      return undefined;
    case 'data.new_status':
    case 'data.old_status':
      // lead.status_changed מגיע גם מהחייגן וגם מ-API; נציג את סט החייגן (הנפוץ).
      return event === 'lead.status_changed' ? DIALER_LEAD_STATUS_ENUM : undefined;
    default: return undefined;
  }
}

// אייקון + תיאור קצר לכל סוג פעולה (הקטלוג עצמו מגיע מהשרת עם התוויות).
// warn = אזהרת תלות: הפעולה ממומשת אך לא תעבוד בלי הגדרה חיצונית.
export const ACTION_META: Record<string, { icon: IconName; hint: string; warn?: string }> = {
  send_webhook: { icon: 'link', hint: 'שולח POST חתום (HMAC) לכתובת חיצונית.' },
  fireberry_upsert_contact: { icon: 'user', hint: 'מחפש לקוח בפיירברי לפי הטלפון מהאירוע; מעדכן אם קיים, יוצר אם לא. שדות נוספים = שם שדה בפיירברי → ערך (אפשר משתנים).' },
  fireberry_log_call: { icon: 'phone', hint: 'רושם את השיחה (כיוון, סטטוס, משך, הקלטה) על הלקוח בפיירברי.' },
  fireberry_add_note: { icon: 'clipboard', hint: 'מוסיף הערה על הלקוח בפיירברי. טקסט ריק = קוד סיום + הערות + סיכום מהאירוע.' },
  fireberry_update_record: { icon: 'edit', hint: 'מעדכן שדות ברשומת הלקוח בפיירברי (למשל סטטוס).' },
  create_callback: { icon: 'phone', hint: 'קובע שיחה חוזרת לנציג.' },
  update_contact: { icon: 'user', hint: 'מוסיף תגית / משנה סטטוס / מוסיף הערה לאיש הקשר.' },
  create_ticket: { icon: 'clipboard', hint: 'פותח טיקט פנימי.' },
  send_whatsapp: {
    icon: 'send',
    hint: 'שולח הודעת WhatsApp דרך חיבור ה-WhatsApp של המרכזיה.',
    warn: 'דורש שמנהל-העל יחבר למרכזיה חשבון WhatsApp (instance). בלי חיבור — הפעולה תיכשל.',
  },
  send_email: {
    icon: 'mail',
    hint: 'שולח אימייל.',
    warn: 'דורש הגדרת שרת דואר (SMTP) במערכת. אם לא הוגדר — המייל לא יישלח בפועל.',
  },
};

export const TRIGGER_ICON: Record<string, IconName> = {
  event: 'bolt',
  inbound: 'link',
  schedule: 'clock',
};

/** נרמול conditions לצורה המקובצת {match, groups} — לעבודה אחידה בבונה. */
export function toGroups(conditions: HubConditions | undefined | null): HubConditionGroups {
  if (!conditions) return { match: 'all', groups: [] };
  if (Array.isArray(conditions)) {
    // רשימה שטוחה ישנה → קבוצה אחת (AND).
    return { match: 'all', groups: conditions.length ? [conditions] : [] };
  }
  return {
    match: conditions.match === 'any' ? 'any' : 'all',
    groups: (conditions.groups ?? []).filter((g) => Array.isArray(g)),
  };
}

/** ספירת מספר שורות התנאים הכולל (בכל הצורות) — לתצוגת הכרטיס. */
export function countConditions(conditions: HubConditions | undefined | null): number {
  const g = toGroups(conditions);
  return g.groups.reduce((sum, group) => sum + group.length, 0);
}

/** רשימת שורה ריקה חדשה. */
export function emptyRow(defaultOp: string): HubCondition {
  return { field: '', op: defaultOp, value: '' };
}
